// FCLLimitsBar.js — SON DÖNEM TECRÜBESİ (4 Eyl 2026)
//
// 🔑 Serkan: *"Artık FTL limits gibi FCL limits yazsın barların başlığı ve ref
//    versin tabii ki."* · *"Geçmişe dönük verilerden gece gündüz iniş kalkış
//    hepsini görebilmemiz lazım."*
//
// ⚠️ NEDEN AYRI BİR BAR: üstteki FTL limitleri SHT-FTL/HG kural setinden gelir
//    (görev ve dinlenme). Burası LİSANS talimatıdır — SHT-FCL FCL.060. İki
//    ayrı mevzuat; aynı tabloya koymak "hangi kural nereden" sorusunu
//    bulanıklaştırırdı. Künye başlıkta durur.
//
// 🔑 GECE/GÜNDÜZ SAKLANMAZ, HESAPLANIR: meydan koordinatı + saat → gün
//    doğumu/batımı ∓30 dk (Serkan tanımı). Geçmiş uçuşlar için de bugün doğru
//    cevabı verir; göç veya geriye doldurma gerekmez.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
// pfNightRecency BURADA KULLANILMIYOR: gece sarti bu operatorde uygulanmiyor
// (FCL.060(a)(2)(ii), IR muafiyeti). Kural motorda ve RULE SET'te DURUYOR —
// karar `strict`e cevrilirse satir geri gelir.
import { pfSectors, simSectors, pfRecency, pfDayNightCounts, PF_RECENCY } from './FTLEngine';
import { fclSirketKurallari } from './FTLRuleCatalog';
import { __S as S, __C as C } from './FTLLimitsBar';
import GosterGizle from './GosterGizle';   // göster/gizle düğmesi — tek bileşen (5 Eki 2026, tasarım B)
import InfoDot from './InfoDot';
import { ucusEkibindenMi } from '../roller';
import { SAYILI, hepsiniOku, okumaDenetimi } from './kesikOkuma';
import OkumaHatasi from './OkumaHatasi';


/** Meydan koordinatları — `airports.lat/lon`. Yoksa gece/gündüz ÖLÇÜLEMEZ.
 *  21 Eki 2026 (W4b): okuma düşerse `null` — "koordinatı yok" DEĞİL (ekran öyle yazıyordu). */
const fetchGeo = async (icaos) => {
  const temiz = [...new Set(icaos.filter(Boolean).map(x => String(x).toUpperCase()))];
  if (!temiz.length) return {};
  let sonuc;
  try { sonuc = await supabase.from('airports').select('icao,lat,lon').in('icao', temiz); } catch (e) { return null; }
  if (sonuc?.error) return null;
  const data = sonuc?.data;
  return Object.fromEntries((data || [])
    .filter(a => Number.isFinite(a.lat) && Number.isFinite(a.lon))
    .map(a => [a.icao, { lat: a.lat, lon: a.lon }]));
};

function RecencyCell({ r, testid }) {
  // 🔑 "KAYIT YOK" YEŞİL GÖSTERİLMEZ. Sıfırdan başlayan bir çubuk yeşil görünür,
  // oysa ölçüm yapılmamıştır — FTL barındaki "BASELINE NOT SET" ile aynı ders:
  // yeşil gösterip yanıltmak, hiç göstermemekten kötüdür (İlke 1).
  if (r.state === 'unknown') {
    return (
      <td style={S.cell} title={r.note} data-testid={testid} data-durum="unknown">
        <span style={{ fontSize:9, letterSpacing:1, color:C.t3, fontFamily:'var(--mono)' }}>
          NO PF SECTOR ON RECORD
        </span>
      </td>
    );
  }
  const renk = r.state === 'red' ? C.red : r.state === 'amber' ? C.amber : C.green;
  const dolu = r.ageDays == null ? 100 : Math.min(100, (r.ageDays / r.windowDays) * 100);
  const ipucu = r.ageDays == null
    ? `Only ${r.count} qualifying sector(s) in the last ${r.windowDays} days — ${r.required} required (${r.reference}).`
    : `The oldest sector still keeping you current is ${r.ageDays} day(s) old (${r.keepDate}). `
      + `${r.daysLeft} day(s) left before the count drops below ${r.required}. ${r.reference}.`;
  return (
    <td style={S.cell} title={ipucu} data-testid={testid} data-durum={r.state}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={S.track}><div style={{ width:`${dolu}%`, background:renk, height:'100%' }} /></div>
        <span style={{ fontSize:11, fontFamily:'var(--mono)', fontVariantNumeric:'tabular-nums',
                       color: r.state === 'green' ? C.t1 : renk, whiteSpace:'nowrap' }}>
          {r.count}/{r.required}
          {r.ageDays != null && <span style={{ color:C.t3 }}> · {r.ageDays}d</span>}
        </span>
      </div>
    </td>
  );
}

/** Gece/gündüz kırılımı — sayılar, çubuk değil: bu bir limit değil, envanterdir. */
function BreakdownCell({ c }) {
  const par = (x) => (
    <>
      <span style={{ color:C.t1 }}>{x.day}</span>
      <span style={{ color:C.t3 }}>D</span>
      <span style={{ color:C.t3 }}> / </span>
      <span style={{ color:C.accent }}>{x.night}</span>
      <span style={{ color:C.t3 }}>N</span>
      {x.unknown > 0 && (
        <span style={{ color:C.amber }} title="Aerodrome coordinates missing — day/night not measured">
          {' '}· {x.unknown}?
        </span>
      )}
    </>
  );
  return (
    <td style={S.cell} data-testid="pf-daynight-cell">
      <span style={{ fontSize:11, fontFamily:'var(--mono)', fontVariantNumeric:'tabular-nums' }}>
        T/O {par(c.takeoffs)}<span style={{ color:C.t3 }}> · </span>LDG {par(c.landings)}
        {/* FFS oturumu gece/gündüz ÖLÇÜLMEZ (2 Eki 2026) — ayrı sayılır, programın asgarisi kadar yakınlığa girer. */}
        {c.ffsOturum > 0 && (
          <span data-testid="pf-ffs" style={{ color:C.t2 }}
                title={`FFS Level D session(s) — each counts as the programme minimum: ${PF_RECENCY.required} take-offs and landings (FCL.060(a)(1)); day/night is not counted for a simulator`}>
            <span style={{ color:C.t3 }}> · </span>FFS {c.ffsOturum}
          </span>
        )}
      </span>
    </td>
  );
}

export default function FCLLimitsBar({ pilots, duties, asOf = null }) {
  const [open, setOpen] = useState(true);
  const [geo, setGeo] = useState({});
  // 🔑 TABAN (Serkan, 4 Eyl): canlıya geçişten önceki sektörlerin TARİHLERİ.
  //    Olmadan ilk 90 gün herkes "kaydı yok" görünürdü. 90 gün sonra bu
  //    tarihler pencereden çıkar ve taban kendiliğinden anlamsızlaşır.
  const [tabanlar, setTabanlar] = useState({});
  // 🔴 21 Eki 2026 (kod taraması W4b): iki okuma da hatayı okumuyordu. Taban düşünce eski iniş tarihleri yok sayılıp 90 gün /
  //    3 iniş EKSİK sayılıyordu (pilot yanlışlıkla NOT CURRENT görünebilirdi); koordinat düşünce "meydanın koordinatı yok"
  //    deniyordu. Artık düşen ya da kesik okumada çubuk yerine okuma kutusu (yarım veriyle yakınlık gösterilmez) + RETRY.
  const [tabanHatasi, setTabanHatasi] = useState(null);
  const [geoHatasi, setGeoHatasi] = useState(null);
  const [deneme, setDeneme] = useState(0);
  const an = useMemo(() => (asOf ? new Date(asOf) : new Date()), [asOf]);

  const crew = useMemo(
    () => (pilots || []).filter(ucusEkibindenMi), [pilots]);

  // 🔑 FCL.060(a)(2)(ii) — IR muafiyeti. Serkan (5 Eyl 2026): *"Bu ucakta ucan
  //    herkes ATPL, yani IR yetkisi var."*  ATPL zaten IR gerektirir.
  const irMuaf = useMemo(
    () => fclSirketKurallari().find(x => x.key === 'night_recency_ir_exemption')?.value === 'ir_exempt',
    []);

  // Pencereye giren sektörlerin meydanları — yalnız gerekenler çekilir.
  const icaolar = useMemo(() => {
    const s = new Set();
    crew.forEach(p => pfSectors(duties, p.id, an, {}).forEach(x => {
      if (x.dep) s.add(x.dep); if (x.dest) s.add(x.dest);
    }));
    return [...s];
  }, [crew, duties, an]);

  useEffect(() => {
    let olu = false;
    (async () => {
      const { dustu, oku } = okumaDenetimi();
      const data = oku('FCL BASELINES (fcl_pilot_baselines)', await hepsiniOku(() => supabase.from('fcl_pilot_baselines')
        .select('id,pilot_id,ac_type,effective_date,last_pf_sectors,last_night_pf,created_at', SAYILI)
        .order('created_at', { ascending: false })));
      if (olu) return;
      setTabanHatasi(dustu.length ? dustu : null);
      if (dustu.length) { setTabanlar({}); return; }
      // Pilot basina EN YENI satir gecerli (Ilke 4 — duzeltme yeni satirdir).
      // Tip ayrimi burada BILEREK yapilmiyor: tek tipli filoda her tipin
      // tarihleri ayni pilotun yakinligini besler. Filo cesitlenirse bu
      // satir tipe gore daraltilacak — `measurability` blogunda yazili.
      const m = {};
      (data || []).forEach(r => {
        if (!m[r.pilot_id]) m[r.pilot_id] = { dates: [], night: null };
        (r.last_pf_sectors || []).forEach(d => m[r.pilot_id].dates.push(d));
        if (!m[r.pilot_id].night && r.last_night_pf) m[r.pilot_id].night = r.last_night_pf;
      });
      setTabanlar(m);
    })();
    return () => { olu = true; };
  }, [deneme]);

  useEffect(() => {
    if (!icaolar.length) return undefined;
    let olu = false;
    (async () => {
      const m = await fetchGeo(icaolar);
      if (olu) return;
      setGeoHatasi(m ? null : [{ ad: 'AERODROME COORDINATES (airports)', metin: 'could not be read' }]);
      setGeo(m || {});
    })();
    return () => { olu = true; };
  }, [icaolar.join(','), deneme]);      // eslint-disable-line react-hooks/exhaustive-deps

  const cols = useMemo(() => crew.map(p => {
    // 🔑 FFS oturumlari GERCEK SEKTORLERLE AYNI listede: talimat ikisini
    //    esdeger sayiyor (FCL.060(a)(1) ve (a)(2) — "…veya FFS'inde…").
    //    Sirali kalmasi sart, "en yeni gece olayi" ondan bulunuyor.
    const sec = [...pfSectors(duties, p.id, an, geo), ...simSectors(duties, p.id, an)]
      .sort((a, b) => b.ms - a.ms);
    return {
      pilot: p, sec,
      gunduz: pfRecency(duties, p.id, an, { baselineDates: tabanlar[p.id]?.dates || [] }),
      kirilim: pfDayNightCounts(sec),
    };
  }), [crew, duties, an, geo, tabanlar]);

  if (!cols.length) return null;
  if (tabanHatasi || geoHatasi) return (
    <div style={{ ...S.wrap, position:'static', boxShadow:'none' }} data-testid="fcl-limits">
      <OkumaHatasi testid="fcl-okuma-hatasi" style={{ padding:'10px 12px' }}
        satirlar={[...(tabanHatasi || []), ...(geoHatasi || [])]} onRetry={() => setDeneme(n => n + 1)}
        baslik="FCL LIMITS COULD NOT BE LOADED — recent experience is not shown with partial data." />
    </div>
  );
  const olculemeyen = cols.reduce((a, c) => a + c.kirilim.takeoffs.unknown + c.kirilim.landings.unknown, 0);

  return (
    <div style={{ ...S.wrap, position:'static', boxShadow:'none' }} data-testid="fcl-limits">
      <div style={S.head}>
        <span style={S.title}>FCL Limits</span>
        <span style={S.meta}>
          SHT-FCL FCL.060(a)(1) — recent experience · Yayım 04/09/2024, Değişiklik No 00 ·
          last {PF_RECENCY.windowDays} days{irMuaf ? ' · night recency not required — IR held, FCL.060(a)(2)(ii)' : ''}
        </span>
        <GosterGizle acik={open} onClick={() => setOpen(o => !o)} testid="fcl-limits-toggle" style={{ marginLeft:'auto' }} />
      </div>

      {open && <>
        <table style={S.table}>
          <tbody>
            <tr>
              <th style={S.grp} /><th style={S.lbl} />
              {cols.map(c => (
                <th key={c.pilot.id} style={S.th}>
                  {(c.pilot.code ? c.pilot.code + ' — ' : '') + (c.pilot.full_name || '').toUpperCase()}
                </th>
              ))}
            </tr>

            <tr>
              <td style={S.grp}>PF</td>
              {/* 🔴 4 Eyl: bunlar YALNIZ `title` ipucuydu, tiklaninca hicbir sey
                  olmuyordu (Serkan: *"burdaki i remarklarini tikliyorum hic bir
                  sey cikmiyor"*). Artik RULE SET'tekiyle AYNI bilesen. */}
              <td style={S.lbl}>
                3 T/O · APP · LDG — LAST {PF_RECENCY.windowDays} D
                <InfoDot testid="fcl-info-pf" title="RECENT EXPERIENCE — PF"
                  note={`At least ${PF_RECENCY.required} take-offs, approaches and landings in the `
                    + `previous ${PF_RECENCY.windowDays} days are required. The bar shows the age of the `
                    + `${PF_RECENCY.required}rd most recent PF sector — when it passes `
                    + `${PF_RECENCY.windowDays} days the count drops below ${PF_RECENCY.required}.`}
                  rule={'SHT-FCL FCL.060(a)(1): "Son 90 gün içerisinde aynı tip/sınıf hava aracında veya '
                    + 'söz konusu hava aracı tipinin/sınıfının tam uçuş simülatöründe (FFS) en az 3 kalkış, '
                    + 'yaklaşma ve iniş icra etmemiş ise." A PF sector counts as all three; a full flight '
                    + `simulator session counts too. Green under ${PF_RECENCY.amberFromDays} days, `
                    + `amber to ${PF_RECENCY.redFromDays}, red after that (company thresholds).`}
                  action={'Fly a PF sector, or record an FFS Level D session — it counts as the approved programme\'s '
                    + `minimum (${PF_RECENCY.required} take-offs and landings); the numbers are not entered. `
                    + 'FCL.060(b)(1) also allows the 90 days to be extended to 120 with line flying under '
                    + 'the supervision of a TRI or check pilot — that is not recorded here.'} />
              </td>
              {cols.map(c => <RecencyCell key={c.pilot.id} r={c.gunduz} testid="pf-recency-cell" />)}
            </tr>

            <tr>
              <td style={S.grp}>PF SECTORS</td>
              <td style={S.lbl}>DAY / NIGHT</td>
              {cols.map(c => <BreakdownCell key={c.pilot.id} c={c.kirilim} />)}
            </tr>
          </tbody>
        </table>

        <div style={S.note}>
          {/* Gece satiri BILEREK YOK (Serkan, 5 Eyl 2026): *"Night degil, zaten
              biz IR yetkimiz var"* · *"Corba olmasin."*  Uygulanmayan bir sarti
              barda tutmak ekrani bulandirirdi. Karar ve gerekcesi RULE SET'te,
              gece/gunduz kirilimi ise OLGUSAL veri oldugu icin asagida duruyor. */}
          FCL.060(a)(1): at least <b>3 take-offs, approaches and landings</b> in the last
          {' '}{PF_RECENCY.windowDays} days, in the same type/class or its FFS. A PF sector counts as
          all three; an FFS Level D session counts as the approved programme's minimum ({PF_RECENCY.required}) and is
          shown as FFS, outside the day/night count. Day/night is computed from the aerodrome coordinates and the recorded times —
          nothing is stored, so past flights are answered correctly too.
          {irMuaf && <> <b>Night recency (a)(2) does not apply</b> — all flight crew on this type hold
          an ATPL, which requires a valid IR; see RULE SET for the decision and its reasoning.</>}
          {' '}<b>Awareness only</b>: type/class is not distinguished, and the 120-day extension under
          FCL.060(b)(1) (line flying under supervision) is not recorded.
          {olculemeyen > 0 && (
            <span style={{ color:C.amber }}> · {olculemeyen} sector end(s) marked <b>?</b> — the
              aerodrome has no coordinates, so day/night could not be measured.</span>
          )}
        </div>
      </>}
    </div>
  );
}
